import { useTranslation } from "react-i18next";
import {
	ChartCard,
	type Column,
	ColumnChart,
	HBarList,
	Histogram,
	holatVar,
	Legend,
} from "../../../../../shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import {
	AnalyticsGrid,
	kunYorlig,
	sanaQisqa,
	tartiblanganHaftaKunlari,
	useBolimNomi,
} from "./shared";

/**
 * Ajratiladigan kechikish bucketlari — `LATE_BUCKETS` yorliqlari
 * (`flagged` ta'rifi `late_minutes > 30` bilan bir xil chegara).
 */
const OGIR_BUCKETLAR = new Set(["31-60", "60+"]);

/**
 * `lateness` analitik ko'rinishi (reja F1):
 * KPI → [kunlar ustunlari | kechikish gistogrammasi] →
 * [hafta kunlari ustunlari | eng ko'p kechikkanlar | bo'limlar].
 *
 * Konvert (`lateness.ts`): `daily.primary` — kechikishlar soni,
 * `daily.flagged` — shundan 30 daqiqadan ortiqlari; `weekday.count`
 * (`rate` doim `null`); `topEmployees.total` — DAQIQA;
 * `byDepartment.total` — kechikishlar SONI.
 *
 * ⚠️ `topEmployees` uchun `Lollipop` emas, `HBarList`: `flagged` (kunlar
 * soni) `total` (daqiqa) ning qismi EMAS — birliklar boshqa.
 */
export function LatenessAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bolimNomi = useBolimNomi();
	const bosh = t("analytics.attendance.empty");
	const kechYorliq = t("analytics.attendance.lateness.late");
	const ogirYorliq = t("analytics.attendance.lateness.heavy");
	const daqiqa = (v: number) =>
		t("analytics.attendance.minutesValue", { value: sonMatni(Math.round(v)) });

	const ustunlar: Column[] = data.daily.map((kun) => {
		const ogir = Math.min(kun.flagged, kun.primary);
		return {
			key: kun.date,
			label: kunYorlig(kun.date),
			stack: [
				{ value: kun.primary - ogir, color: holatVar("kechikkan"), label: kechYorliq },
				{ value: ogir, color: holatVar("kelmagan"), label: ogirYorliq },
			],
			tooltip: `${sanaQisqa(kun.date)}: ${sonMatni(kun.primary)} (${ogirYorliq}: ${sonMatni(ogir)})`,
			topLabel: kun.primary > 0 ? sonMatni(kun.primary) : undefined,
		};
	});

	const binlar = data.histogram.map((b) => ({
		label: b.bucket,
		count: b.count,
		highlight: OGIR_BUCKETLAR.has(b.bucket),
	}));

	const haftaUstunlari: Column[] = tartiblanganHaftaKunlari(data.weekday).map((h) => {
		const nom = t(`analytics.weekday.${h.weekday}`);
		return {
			key: String(h.weekday),
			label: nom.slice(0, 2),
			stack: [{ value: h.count, color: holatVar("kechikkan"), label: kechYorliq }],
			tooltip: `${nom}: ${sonMatni(h.count)}`,
			topLabel: sonMatni(h.count),
		};
	});

	const xodimlar = data.topEmployees.map((x) => ({
		key: x.employeeId,
		label: x.employeeName,
		value: x.total,
		valueText: daqiqa(x.total),
		color: holatVar("kechikkan"),
	}));

	const bolimlar = data.byDepartment.map((b) => ({
		key: b.departmentId ?? "none",
		label: bolimNomi(b.departmentName),
		value: b.total,
		valueText: sonMatni(b.total),
		segments: [
			{
				value: b.total - Math.min(b.flagged, b.total),
				color: holatVar("kechikkan"),
				label: kechYorliq,
			},
			{ value: Math.min(b.flagged, b.total), color: holatVar("kelmagan"), label: ogirYorliq },
		],
	}));

	const legenda = (
		<Legend
			items={[
				{ label: kechYorliq, color: holatVar("kechikkan") },
				{ label: ogirYorliq, color: holatVar("kelmagan") },
			]}
		/>
	);

	return (
		<div data-analytics-layout="lateness" className="flex flex-col gap-4">
			<KpiRow
				kind={kind}
				totals={data.totals}
				spark={data.spark}
				items={[
					{ key: "lateCount", danger: true },
					{ key: "lateMin", danger: true },
					{ key: "employees" },
					{ key: "avgLate", danger: true },
				]}
			/>

			<AnalyticsGrid cols={2}>
				<ChartCard
					title={t("analytics.attendance.lateness.columnsTitle")}
					subtitle={t("analytics.attendance.lateness.columnsSubtitle")}
					extra={legenda}
				>
					<div data-daily-columns>
						<ColumnChart columns={ustunlar} empty={bosh} />
					</div>
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.lateness.histogramTitle")}
					subtitle={t("analytics.attendance.lateness.histogramSubtitle")}
				>
					<Histogram
						bins={binlar}
						color={holatVar("kechikkan")}
						highlightColor={holatVar("kelmagan")}
						empty={bosh}
					/>
				</ChartCard>
			</AnalyticsGrid>

			<AnalyticsGrid cols={3}>
				<ChartCard title={t("analytics.attendance.lateness.weekdayTitle")}>
					<div data-weekday-columns>
						<ColumnChart columns={haftaUstunlari} empty={bosh} />
					</div>
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.lateness.topTitle")}
					subtitle={t("analytics.attendance.lateness.topSubtitle")}
				>
					<HBarList rows={xodimlar} empty={bosh} />
				</ChartCard>

				<ChartCard
					title={t("analytics.attendance.lateness.departmentsTitle")}
					subtitle={t("analytics.attendance.lateness.departmentsSubtitle")}
					extra={legenda}
				>
					<HBarList rows={bolimlar} empty={bosh} />
				</ChartCard>
			</AnalyticsGrid>
		</div>
	);
}
